import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Filter } from '@vk-hr-tek/core/filter';
import { Calendar } from '@vk-hr-tek/core/calendar';

import {
  CompanyPolicyListItem as CompanyPolicyListEntity,
  EmployeePolicyListItem as EmployeePolicyListEntity,
} from '@app/gen/policy';
import { KONTUR_RECOVERY_DEFAULT_MESSAGE } from '@app/constants';

import { DRAFT_VERSION_STATUS, VERSION_STATUS_TEXT } from '../constants';

@injectable()
export class PolicyMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
  ) {}

  processCompanyListEntity(policy: CompanyPolicyListEntity) {
    const isDraft = policy.version_status === DRAFT_VERSION_STATUS;

    return {
      id: policy.policy_id,
      name: policy.name,
      companyName: policy.company.name,
      version: this.processVersion(policy),
      isParent: policy.hierarchy_type === 'parent',
      employeesCount: {
        accepted: isDraft ? 0 : policy.accepted_employees,
        total: isDraft ? 0 : policy.total_employees,
        childPoliciesAmount: policy.child_policies_amount,
      },
      signatureType: this.processSignatureType(policy),
    };
  }

  processEmployeeListEntity(
    policy: EmployeePolicyListEntity,
    variant: 'desktop' | 'mobile',
  ) {
    return {
      idMixed: `${policy.policy_version_id}${policy.employee_id}`,
      policyId: policy.policy_id,
      companyName: policy.company.name,
      companyId: policy.company.company_id,
      companyTsp: policy.company.tsp_url || '',
      hashSource: policy.company.hash_source,
      useAcquaintance: policy.company.use_acquaintance,
      cryptoType: policy.company.crypto_pro_plugin_open_type,
      policyVersionId: policy.policy_version_id,
      name: policy.name,
      accepted: policy.status === 'signed',
      status: policy.status,
      label: this.processSignatureTypeLabel(policy),
      notification: this.processNotification(policy, variant),
      error: this.processError(policy),
      signatureType: policy.signature_type,
      signatureTypeSubstituted: policy.signature_type_substituted,
      systemDownMessage:
        policy.kontur_system_down?.message || KONTUR_RECOVERY_DEFAULT_MESSAGE,
      isRead: !!policy.read_at,
      employeeId: policy.employee_id,
      hideIfAccepted: !!policy.hide_if_accepted,
      isDownloadAccess: !!policy.company.employee_download_pdf_access,
      acceptancePeriodDays: policy.acceptance_period_days,
      acceptUntil: policy.accept_until,
      isAcceptancePeriodActive: this.processIsAcceptancePeriodActive(policy),
      acceptUntilDate: this.processAcceptUntilDate(policy),
      attachedFileIds: policy.attached_file_ids,
    };
  }

  processListFilters(filters: Filter[]): Filter[] {
    const activeFilters = filters.filter((filter) => {
      const { key, type } = filter;

      if (type === 'select' || type === 'multiple') {
        if (filter?.options.length === 0) {
          return false;
        }
      }
      return key !== 'tag';
    });

    if (activeFilters.length === 1) {
      return filters.map((filter) => {
        return {
          ...filter,
          width: 12,
        };
      });
    }

    const wideFields = [
      'unit_ids',
      'names',
      'positions',
      'exclude_common_company',
    ];

    return filters.map((filter) => {
      if (wideFields.includes(filter.key)) {
        return {
          ...filter,
          width: 8,
          fullWidth: false,
          ...(filter.type === 'bool' && {
            labelPlacement: 'end',
          }),
        };
      }

      if (
        filter.type === 'bool' &&
        ['include_subunits', 'include_parent_policies'].includes(filter.key)
      ) {
        return {
          ...filter,
          width: 12,
          fullWidth: false,
          labelPlacement: 'end',
        };
      }

      if (filter.type === 'search' && filter.key === 'unit_id') {
        return {
          ...filter,
          width: 8,
          fullWidth: false,
        };
      }

      // eslint-disable-next-line no-magic-numbers
      if (filter.key === 'signature_type' && activeFilters.length === 2) {
        return {
          ...filter,
          width: 8,
          fullWidth: false,
        };
      }

      if (filter.type === 'search' && filter.key === 'category_id') {
        return {
          ...filter,
          width: 8,
          fullWidth: false,
        };
      }

      return {
        ...filter,
        width: 4,
        fullWidth: false,
      };
    });
  }

  processSignatureType(policy: CompanyPolicyListEntity) {
    const signatureType = policy.signature_type;

    switch (signatureType) {
      case 'pep':
        return 'ПЭП';
      case 'unep':
      case 'ukep':
        return 'УНЭП/УКЭП';
      default:
        return signatureType;
    }
  }

  processSignatureTypeLabel = (policy: EmployeePolicyListEntity) => {
    const signatureType = policy.signature_type;

    switch (signatureType) {
      case 'unep_goskey':
        return 'УНЭП Госключ';
      case 'unep_kontur':
        return 'УНЭП СКБ Контур';
      case 'unep_cryptopro_simple':
        return 'УНЭП КриптоПро';
      case 'unep_cryptopro_local':
        return 'УНЭП Цифровой носитель';
      case 'ukep':
        return 'УКЭП Цифровой носитель';
      case 'unep_disabled':
        return 'УНЭП';
    }
  };

  processVersion(policy: CompanyPolicyListEntity) {
    return {
      label: VERSION_STATUS_TEXT[policy.version_status],
      date: this.getDateBetween(policy.active_from, policy.active_to),
    };
  }

  processNotification(
    policy: EmployeePolicyListEntity,
    variant: 'desktop' | 'mobile',
  ) {
    const { signature_type: signatureType, status } = policy;

    const desktopText =
      'Ознакомьтесь с документом и отправьте на подписание в приложение Госключ';
    const mobileText = 'Перейдите в приложение Госключ и подпишите документ';

    if (signatureType === 'unep_goskey' && status === 'not_signed') {
      return variant === 'desktop' ? desktopText : mobileText;
    }
  }

  processError(policy: EmployeePolicyListEntity) {
    const signatureType = policy.signature_type;
    const status = policy.status;
    const detailedError = policy.signature_detailed_error;

    if (signatureType === 'unep_goskey' && status === 'error') {
      switch (detailedError) {
        case 'snils_not_found':
          return {
            message: 'Не найдена учетная запись на Госуслугах.',
            description:
              'Чтобы использовать подписание через Госключ, подтвердите вашу учетную запись.',
          };
        default:
          return {
            message: 'Ошибка при соединении с Госключом.',
            description:
              'Попробуйте ещё раз и обратитесь в службу поддержки, если ошибка повторится',
          };
      }
    }
    if (signatureType === 'unep_goskey' && status === 'canceled') {
      return {
        message:
          'Мы получили ваш отказ от подписания документа в приложении Госключ.',
        description: 'Вы можете повторно отправить документ на подпись',
      };
    }
  }

  getDateBetween(from: string, to?: string) {
    return to
      ? `c ${this.format.toDate(from)} по ${this.format.toDate(to)}`
      : `c ${this.format.toDate(from)}`;
  }

  processAcceptUntilDate(policy: EmployeePolicyListEntity) {
    return `Ознакомиться до ${this.format.toTime(
      policy.accept_until ?? '',
    )} ${this.format.toDate(policy.accept_until ?? '')}`;
  }

  processIsAcceptancePeriodActive(policy: EmployeePolicyListEntity) {
    return this.calendar.isMoreThan(
      new Date(policy.accept_until ?? ''),
      new Date(),
    );
  }
}
